import _ from 'lodash';
import React from 'react';
import PriceBreakdown from './PriceBreakdown';
import Currencies from '../../general/utils/Currencies';

function toNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

export function convertAmountToUsd(
    amount,
    { currency, outputCurrencyToUsdExchangeRate, fallbackToOriginal = false } = {},
) {
    const normalizedAmount = toNumber(amount);
    if (normalizedAmount === null) {
        return null;
    }

    if (typeof currency === 'string' && currency.trim().toUpperCase() === 'USD') {
        return normalizedAmount;
    }

    const rate = toNumber(outputCurrencyToUsdExchangeRate);
    if (rate !== null && rate > 0) {
        return normalizedAmount * rate;
    }

    return fallbackToOriginal ? normalizedAmount : null;
}

/**
 * Gets the points per USD exchange rate from the points object
 * @param {Object} points - The points object containing points_ex_rate (from DB)
 * @returns {number|null} Points per USD, or null if points_ex_rate is not available
 */
function getPointsPerUSD(points) {
    const pointsExRate = _.get(points, 'points_ex_rate');

    if (pointsExRate && pointsExRate > 0) {
        // Convert from "currency per point" to "points per USD"
        // points_ex_rate is in "currency per point" format (e.g., 0.005714 USD per point for Elal)
        return 1 / pointsExRate;
    }

    // points_ex_rate should always come from DB - if missing, return null
    return null;
}

/**
 * Calculates the maximum points that can be used for a deal based on the deal price
 * @param {Object} deal - The deal object containing pricing information
 * @param {Object} profile - The user profile
 * @param {Object} points - The points object containing balance
 * @returns {number} The maximum points that can be used for this deal (capped by balance)
 */
export function getMaxPointsForDeal(deal, profile, points) {
    if (!points || !points.balance || points.balance <= 0) {
        return 0;
    }

    // Get the deal price based on PriceBreakdown conditions
    let dealPrice;
    if (PriceBreakdown.isBreakdownPrice({ profile, deal })) {
        dealPrice = deal.totalPrice || _.get(deal, 'cheapopoPricing.cheapopoTotalPrice');
    } else if (PriceBreakdown.isShowTotalPrice({ profile })) {
        dealPrice = deal.totalPrice;
    } else {
        dealPrice = deal.pricePerNight;
    }

    if (!dealPrice || dealPrice <= 0) {
        return points.balance;
    }

    // Convert deal price to USD
    const dealCurrency = deal?.currency || 'USD';
    const outputCurrencyToUsdExchangeRate =
        _.get(deal, 'outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'cheapopoPricing.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'cheapopoPricing.output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'original.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'original.output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'chooseprod.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'chooseprod.output_currency_to_usd_exchange_rate');
    const dealPriceInUSD = convertAmountToUsd(dealPrice, {
        currency: dealCurrency,
        outputCurrencyToUsdExchangeRate,
        fallbackToOriginal: true,
    });

    // Calculate maximum points: price in USD * points per USD
    // Uses provider-specific exchange rate from points object (from DB)
    const pointsPerUSD = getPointsPerUSD(points);
    if (!pointsPerUSD) {
        // Can't calculate without exchange rate from DB
        return 0;
    }
    const maxPointsForDeal = Math.floor(dealPriceInUSD * pointsPerUSD);

    // Return the minimum of balance and max points for deal
    return Math.min(points.balance, maxPointsForDeal);
}

/**
 * Converts a deal's full stay total price into loyalty points for ANONYMOUS display.
 * Unlike getMaxPointsForDeal this is NOT capped by balance (anon users have none).
 * @param {Object} deal - The deal object (totalPrice is in deal.currency)
 * @param {Object} profile - The user profile (display_currency_exchange_rate converts deal->display currency)
 * @param {number} pointsExRate - Currency-per-point in the active DISPLAY currency (from the public rate endpoint)
 * @returns {number} Floored total points for the whole stay, or 0 if not computable
 */
export function getTotalPriceInPoints(deal, profile, pointsExRate) {
    const rate = toNumber(pointsExRate);
    if (rate === null || rate <= 0) {
        return 0;
    }

    const dealPrice = deal?.totalPrice || _.get(deal, 'cheapopoPricing.cheapopoTotalPrice');
    const normalizedDealPrice = toNumber(dealPrice);
    if (normalizedDealPrice === null || normalizedDealPrice <= 0) {
        return 0;
    }

    const displayCurrencyExchangeRate = toNumber(_.get(profile, 'display_currency_exchange_rate')) || 1;
    const displayCurrencyTotal = normalizedDealPrice * displayCurrencyExchangeRate;

    return Math.floor(displayCurrencyTotal / rate);
}

/**
 * Calculates the discount value of points in deal currency
 * @param {Object} deal - The deal object containing currency and pricing information
 * @param {Object} profile - The user profile (not used directly but kept for consistency)
 * @param {Object} points - The points object containing balance
 * @returns {number} The discount value in deal currency
 */
export function getPointsDiscountValue(deal, profile, points) {
    if (!points || !points.balance || points.balance <= 0) {
        return 0;
    }

    // Use the maximum points available for this deal (capped by balance)
    const maxPointsForDeal = getMaxPointsForDeal(deal, profile, points);
    if (maxPointsForDeal <= 0) {
        return 0;
    }

    // Calculate points value in USD: convert points to USD
    // Uses provider-specific exchange rate from points object (from DB)
    const pointsPerUSD = getPointsPerUSD(points);
    if (!pointsPerUSD) {
        // Can't calculate without exchange rate from DB
        return 0;
    }
    const pointsValueInUSD = maxPointsForDeal / pointsPerUSD;

    // Convert USD to deal currency (prices are in deal currency)
    const dealCurrency = deal?.currency || 'USD';

    // If deal currency is USD, return USD value directly
    if (dealCurrency === 'USD') {
        return pointsValueInUSD;
    }

    // If deal currency is not USD, convert USD to deal currency
    // Check if we have outputCurrencyToUsdExchangeRate (deal currency to USD)
    const outputCurrencyToUsdExchangeRate =
        _.get(deal, 'outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'cheapopoPricing.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'cheapopoPricing.output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'original.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'original.output_currency_to_usd_exchange_rate') ||
        _.get(deal, 'chooseprod.outputCurrencyToUsdExchangeRate') ||
        _.get(deal, 'chooseprod.output_currency_to_usd_exchange_rate');

    if (outputCurrencyToUsdExchangeRate && outputCurrencyToUsdExchangeRate > 0) {
        // USD to deal currency = 1 / (deal currency to USD)
        const usdToDealCurrencyRate = 1 / outputCurrencyToUsdExchangeRate;
        return pointsValueInUSD * usdToDealCurrencyRate;
    }

    // Fallback: assume deal currency is USD (or use 1:1 if rates unavailable)
    return pointsValueInUSD;
}

/**
 * Generates debug tooltip content for points display (dev mode only)
 * @param {Object} deal - The deal object
 * @param {Object} profile - The user profile
 * @param {Object} points - The points object
 * @param {number} pointsDiscountValue - The calculated points discount value
 * @param {number} nights - Number of nights
 * @param {Function} getMaxPointsForDeal - Function to get max points for deal
 * @returns {React.Element} The tooltip content JSX
 */
export function getPointsDebugTooltipContent(deal, profile, points, pointsDiscountValue, nights, getMaxPointsForDeal) {
    return React.createElement(
        'div',
        { style: { fontSize: '11px', lineHeight: '1.6' } },
        React.createElement('div', null, React.createElement('strong', null, 'Debug Information:')),
        React.createElement('div', null, 'Points Balance: ', points.balance),
        React.createElement('div', null, 'Max Points Per Deal: ', getMaxPointsForDeal(deal, profile, points)),
        React.createElement('br', null),
        React.createElement('div', null, 'Currency: ', deal.currency || 'N/A'),
        React.createElement('div', null, 'Display Currency: ', profile.display_currency || 'N/A'),
        React.createElement('div', null, 'Display Exchange Rate: ', profile.display_currency_exchange_rate),
        React.createElement('br', null),
        React.createElement('div', null, 'Total Deal Price: ', deal.totalPrice?.toFixed(2), ' ', deal.currency),
        React.createElement('div', null, 'Points Discount Value: ', pointsDiscountValue.toFixed(2), ' ', deal.currency),
        React.createElement(
            'div',
            null,
            'Points Discount Value: ',
            (pointsDiscountValue * profile.display_currency_exchange_rate).toFixed(2),
            ' ',
            profile.display_currency,
        ),
        React.createElement('br', null),
        React.createElement(
            'div',
            null,
            'Total Exchange Price (Before Discount): ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                deal.totalPrice,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        React.createElement(
            'div',
            null,
            'Total Exchange Price (After Discount): ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                deal.totalPrice - pointsDiscountValue,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        React.createElement('br', null),
        React.createElement('div', null, 'Nights: ', nights),
        React.createElement('div', null, 'Price Per Night: ', deal.pricePerNight?.toFixed(2), ' ', deal.currency),
        React.createElement(
            'div',
            null,
            'Price Per Night: ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                deal.pricePerNight,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        React.createElement('br', null),
    );
}

/**
 * Generates debug tooltip content for price display (dev mode only)
 * @param {Object} deal - The deal object
 * @param {Object} profile - The user profile
 * @param {number} points_discount_value - The calculated points discount value
 * @param {number} nights - Number of nights
 * @param {number} pricePerNight - Price per night
 * @param {boolean} active_agent_pricing - Whether agent pricing is active
 * @param {number} client_price_total - Client price total (if agent pricing)
 * @param {number} agentCommission - Agent commission (if agent pricing)
 * @param {Object} points - The points object (optional)
 * @param {number} maxPointsForDeal - Maximum points for deal (optional)
 * @param {number} user_chosen_points - User chosen points (optional)
 * @param {number} points_to_display - Points to display (optional)
 * @returns {React.Element} The tooltip content JSX
 */
export function getPriceDebugTooltipContent(
    deal,
    profile,
    points_discount_value,
    nights,
    pricePerNight,
    active_agent_pricing,
    client_price_total,
    agentCommission,
    points,
    maxPointsForDeal,
    user_chosen_points,
    points_to_display,
) {
    const total_price_base = client_price_total || deal.totalPrice;
    const total_price_after_points = total_price_base - points_discount_value;

    return React.createElement(
        'div',
        { style: { fontSize: '11px', lineHeight: '1.6' } },
        React.createElement('div', null, React.createElement('strong', null, 'Debug Price Information:')),
        points && points.balance !== undefined &&
            React.createElement('div', null, 'Points Balance: ', points.balance),
        maxPointsForDeal !== undefined && maxPointsForDeal !== null &&
            React.createElement('div', null, 'Max Points Per Deal: ', maxPointsForDeal),
        user_chosen_points !== undefined && user_chosen_points !== null && user_chosen_points > 0 &&
            React.createElement('div', null, 'User Chosen Points: ', user_chosen_points),
        points_to_display !== undefined && points_to_display !== null && points_to_display > 0 &&
            React.createElement('div', null, 'Points To Display: ', Math.round(points_to_display)),
        (points || maxPointsForDeal !== undefined || user_chosen_points !== undefined) && React.createElement('br', null),
        React.createElement('div', null, 'Currency: ', deal.currency || 'N/A'),
        React.createElement('div', null, 'Display Currency: ', profile.display_currency || 'N/A'),
        React.createElement('div', null, 'Display Exchange Rate: ', profile.display_currency_exchange_rate),
        React.createElement('br', null),
        React.createElement('div', null, 'Total Deal Price (Original): ', deal.totalPrice?.toFixed(2), ' ', deal.currency),
        active_agent_pricing &&
            React.createElement(
                'div',
                null,
                'Client Price Total: ',
                client_price_total?.toFixed(2),
                ' ',
                deal.currency,
            ),
        active_agent_pricing &&
            React.createElement(
                'div',
                null,
                'Agent Commission: ',
                agentCommission?.toFixed(2),
                ' ',
                deal.currency,
            ),
        React.createElement('div', null, 'Total Price Base: ', total_price_base?.toFixed(2), ' ', deal.currency),
        points_discount_value > 0 &&
            React.createElement(
                'div',
                null,
                'Points Discount Value: ',
                points_discount_value.toFixed(2),
                ' ',
                deal.currency,
            ),
        points_discount_value > 0 &&
            React.createElement(
                'div',
                null,
                'Points Discount Value (Display): ',
                (points_discount_value * profile.display_currency_exchange_rate).toFixed(2),
                ' ',
                profile.display_currency,
            ),
        React.createElement(
            'div',
            null,
            'Total Price After Points: ',
            total_price_after_points?.toFixed(2),
            ' ',
            deal.currency,
        ),
        React.createElement('br', null),
        React.createElement(
            'div',
            null,
            'Total Price (Display Currency - Before Discount): ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                deal.totalPrice,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        React.createElement(
            'div',
            null,
            'Total Price (Display Currency - After Discount): ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                total_price_after_points,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        React.createElement('br', null),
        React.createElement('div', null, 'Nights: ', nights),
        React.createElement('div', null, 'Price Per Night: ', pricePerNight?.toFixed(2), ' ', deal.currency),
        React.createElement(
            'div',
            null,
            'Price Per Night (Display Currency): ',
            Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
                pricePerNight,
                profile.display_currency_exchange_rate,
                profile.display_currency,
            ),
        ),
        deal.taxes_and_fees > 0 &&
            React.createElement(
                'div',
                null,
                'Taxes and Fees: ',
                deal.taxes_and_fees?.toFixed(2),
                ' ',
                deal.taxes_and_fees_currency || deal.currency,
            ),
        deal.tax_service_fee > 0 &&
            React.createElement(
                'div',
                null,
                'Tax Service Fee: ',
                deal.tax_service_fee?.toFixed(2),
                ' ',
                deal.currency,
            ),
        deal.sales_tax > 0 &&
            React.createElement(
                'div',
                null,
                'Sales Tax: ',
                deal.sales_tax?.toFixed(2),
                ' ',
                deal.sales_tax_currency || deal.currency,
            ),
        deal.post_pay &&
            React.createElement('div', null, 'Post Pay: Yes'),
        deal.hotel_price &&
            React.createElement(
                'div',
                null,
                'Hotel Price: ',
                deal.hotel_price?.toFixed(2),
                ' ',
                deal.hotel_currency || deal.currency,
            ),
        React.createElement('br', null),
    );
}
